import { useState, useEffect, useRef, useCallback } from 'react'
import { buildWsUrl, getGenerationStatus } from '../api'
import { PROGRESS_EVENT_TYPE, GENERATION_STATUS } from '../types'
import type { ProgressEvent } from '../types'

interface GenerationProgressState {
  readonly progressPercent: number
  readonly previewUrl: string | null
  readonly resultPath: string | null
  readonly errorMessage: string | null
  readonly isComplete: boolean
  readonly isFailed: boolean
  readonly isCancelled: boolean
  readonly isConnected: boolean
}

const INITIAL_STATE: GenerationProgressState = {
  progressPercent: 0,
  previewUrl: null,
  resultPath: null,
  errorMessage: null,
  isComplete: false,
  isFailed: false,
  isCancelled: false,
  isConnected: false,
}

const TERMINAL_EVENTS: ReadonlySet<string> = new Set([
  PROGRESS_EVENT_TYPE.EXECUTED,
  PROGRESS_EVENT_TYPE.EXECUTION_ERROR,
  PROGRESS_EVENT_TYPE.EXECUTION_INTERRUPTED,
])

const MAX_RETRIES = 5
const RETRY_DELAY_MS = 2000

export function useGenerationProgress(generationId: string | null) {
  const [state, setState] = useState<GenerationProgressState>(INITIAL_STATE)
  const wsRef = useRef<WebSocket | null>(null)
  const retriesRef = useRef(0)
  const cancelledRef = useRef(false)
  const terminalRef = useRef(false)

  const cancel = useCallback(() => {
    const ws = wsRef.current
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: 'cancel' }))
    }
  }, [])

  useEffect(() => {
    if (!generationId) {
      setState(INITIAL_STATE)
      return
    }

    cancelledRef.current = false
    terminalRef.current = false
    retriesRef.current = 0

    function connect() {
      if (cancelledRef.current || terminalRef.current) return

      const url = buildWsUrl(generationId!)
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.addEventListener('open', () => {
        retriesRef.current = 0
        setState((prev) => ({ ...prev, isConnected: true }))
      })

      ws.addEventListener('message', (event: MessageEvent) => {
        try {
          const data = JSON.parse(String(event.data)) as ProgressEvent

          setState((prev) => {
            const next = { ...prev }

            if (data.progress_percent !== null && data.progress_percent !== undefined) {
              next.progressPercent = data.progress_percent
            }
            if (data.preview_url !== null && data.preview_url !== undefined) {
              next.previewUrl = data.preview_url
            }
            if (data.result_path !== null && data.result_path !== undefined) {
              next.resultPath = data.result_path
            }
            if (data.error_message !== null && data.error_message !== undefined) {
              next.errorMessage = data.error_message
            }

            if (data.type === PROGRESS_EVENT_TYPE.EXECUTED) {
              next.isComplete = true
              next.progressPercent = 1
            } else if (data.type === PROGRESS_EVENT_TYPE.EXECUTION_ERROR) {
              next.isFailed = true
            } else if (data.type === PROGRESS_EVENT_TYPE.EXECUTION_INTERRUPTED) {
              next.isCancelled = true
            }

            return next
          })

          if (TERMINAL_EVENTS.has(data.type)) {
            terminalRef.current = true
            ws.close()
          }
        } catch {
          // Ignore non-JSON messages
        }
      })

      ws.addEventListener('close', () => {
        setState((prev) => ({ ...prev, isConnected: false }))

        // Retry if not terminal and not cancelled
        if (!terminalRef.current && !cancelledRef.current && retriesRef.current < MAX_RETRIES) {
          retriesRef.current += 1
          setTimeout(connect, RETRY_DELAY_MS)
        }
      })

      ws.addEventListener('error', () => {
        // Don't immediately set isFailed — let close handler retry
        if (retriesRef.current >= MAX_RETRIES) {
          setState((prev) => ({
            ...prev,
            isConnected: false,
            isFailed: true,
            errorMessage: 'WebSocket connection failed',
          }))
        }
      })
    }

    // Before opening a WebSocket, check if the generation is already in a
    // terminal state (e.g. the component remounted after a tab switch while
    // generation was already complete). If so, restore state from the API
    // directly and skip the WebSocket entirely.
    void getGenerationStatus(generationId).then((result) => {
      if (cancelledRef.current) return

      if (result.ok) {
        const { status, result_path, error_message } = result.value

        if (status === GENERATION_STATUS.COMPLETED) {
          terminalRef.current = true
          setState({
            ...INITIAL_STATE,
            progressPercent: 1,
            isComplete: true,
            resultPath: result_path,
          })
          return
        }

        if (status === GENERATION_STATUS.FAILED) {
          terminalRef.current = true
          setState({
            ...INITIAL_STATE,
            isFailed: true,
            errorMessage: error_message,
          })
          return
        }

        if (status === GENERATION_STATUS.CANCELLED) {
          terminalRef.current = true
          setState({ ...INITIAL_STATE, isCancelled: true })
          return
        }
      }

      // Not yet terminal (or status fetch failed) — fall through to WebSocket
      connect()
    })

    return () => {
      cancelledRef.current = true
      const ws = wsRef.current
      if (ws) {
        ws.close()
        wsRef.current = null
      }
    }
  }, [generationId])

  return { ...state, cancel }
}
